/**
 * Unified content loading utilities for all books
 * Centralizes fetch logic to avoid duplication across reading pages
 */

const isHtmlResponse = (response) => {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('text/html');
};

const fetchJsonFromPaths = async (paths) => {
  let lastStatus = null;

  for (const path of paths) {
    try {
      const response = await fetch(path);
      lastStatus = response.status;

      if (!response.ok || isHtmlResponse(response)) {
        continue;
      }

      return await response.json();
    } catch (_error) {
      continue;
    }
  }

  throw new Error(`Unable to load JSON content (last status: ${lastStatus ?? 'unknown'})`);
};

const fetchTextFromPaths = async (paths) => {
  for (const path of paths) {
    try {
      const response = await fetch(path);
      if (!response.ok || isHtmlResponse(response)) {
        continue;
      }
      return {
        content: await response.text(),
        sourcePath: path
      };
    } catch (_error) {
      continue;
    }
  }

  return null;
};

/**
 * Load adapted content for a book section
 * @param {string} bookId - Book identifier (e.g., 'SongOfRoland')
 * @param {string} level - CEFR level (e.g., 'A1', 'B2')
 * @param {string} chunk - Chunk/section identifier
 * @returns {Promise<Object>} Content data with fragments/stanzas
 */
export const loadAdaptedContent = async (bookId, level, chunk) => {
  try {
    const bookPath = `/content/books/${bookId}/adaptedContent/${level}/${chunk}.json`;
    const textbookPath = `/content/textbooks/${bookId}/adaptedContent/${level}/${chunk}.json`;

    const data = await fetchJsonFromPaths([bookPath, textbookPath]);
    normalizeContentFragments(data);
    
    return data;
  } catch (error) {
    throw new Error(`Failed to load ${bookId} adapted content for ${level}/${chunk}: ${error.message}`);
  }
};

/**
 * Load original content for a book section.
 * Supports both level-scoped paths and legacy flat paths.
 * @param {string} bookId - Book identifier (e.g., 'SongOfRoland')
 * @param {string} chunk - Chunk/section identifier
 * @param {string} [level] - Optional CEFR level (e.g., 'A1', 'B2')
 * @returns {Promise<Object|null>} Original content data or null if not found
 */
export const loadOriginalContent = async (bookId, chunk, level) => {
  try {
    const paths = [];

    if (level) {
      paths.push(
        `/content/books/${bookId}/originalContent/${level}/${chunk}.json`,
        `/content/textbooks/${bookId}/originalContent/${level}/${chunk}.json`
      );
    }

    // Backward-compatible fallback for books with flat originalContent layout.
    paths.push(
      `/content/books/${bookId}/originalContent/${chunk}.json`,
      `/content/textbooks/${bookId}/originalContent/${chunk}.json`
    );

    const data = await fetchJsonFromPaths(paths);
    normalizeContentFragments(data);

    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Normalize content payloads into a stanza/fragment array
 * @param {Object} contentData - Raw content JSON
 * @param {string} contentKey - Optional book-specific content key
 * @returns {Array} Normalized fragments array
 */
export const normalizeContentFragments = (contentData, contentKey) => {
  if (!contentData || typeof contentData !== 'object') return [];

  if (contentKey && Array.isArray(contentData[contentKey])) {
    return contentData[contentKey];
  }

  if (Array.isArray(contentData.fragments)) {
    return contentData.fragments;
  }

  if (Array.isArray(contentData.stanzas)) {
    return contentData.stanzas;
  }

  if (contentData.stanzas && typeof contentData.stanzas === 'object') {
    return Object.entries(contentData.stanzas)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([key, stanza]) => ({
        number: key,
        ...stanza
      }));
  }

  return [];
};

/**
 * Merge original text into adapted fragments across numbering schemes.
 * Some legacy original-content bundles use cumulative fragment numbers,
 * while adapted chapters restart numbering at one.
 */
export const mergeOriginalText = (adaptedFragments, originalFragments) => {
  if (!adaptedFragments.length && !originalFragments.length) return [];

  const numberingAlignsByPosition = adaptedFragments.length === originalFragments.length &&
    adaptedFragments.every((fragment, index) => (
      String(fragment.number) === String(originalFragments[index].number)
    ));

  if (
    adaptedFragments.length > 0 &&
    adaptedFragments.length === originalFragments.length &&
    !numberingAlignsByPosition
  ) {
    return adaptedFragments.map((fragment, index) => ({
      ...fragment,
      originalText: originalFragments[index].originalText || fragment.originalText || ''
    }));
  }

  const adaptedByNumber = new Map(
    adaptedFragments.map(fragment => [String(fragment.number), { ...fragment }])
  );

  originalFragments.forEach(fragment => {
    const key = String(fragment.number);
    if (adaptedByNumber.has(key)) {
      adaptedByNumber.set(key, {
        ...adaptedByNumber.get(key),
        originalText: fragment.originalText || adaptedByNumber.get(key).originalText || ''
      });
    } else {
      adaptedByNumber.set(key, { ...fragment });
    }
  });

  return adaptedFragments.length
    ? adaptedFragments.map(fragment => adaptedByNumber.get(String(fragment.number)))
    : Array.from(adaptedByNumber.values());
};

/**
 * Load summary and audio for a book section
 * @param {string} bookId - Book identifier
 * @param {string} level - CEFR level
 * @param {string} chunk - Chunk/section identifier
 * @returns {Promise<Object|null>} Summary data with content and audio URL, or null if not found
 */
export const loadSummary = async (bookId, level, chunk) => {
  try {
    const bookSummaryPath = `/content/books/${bookId}/summary/${level}/${chunk}.txt`;
    const textbookSummaryPath = `/content/textbooks/${bookId}/summary/${level}/${chunk}.txt`;

    const summaryResult = await fetchTextFromPaths([bookSummaryPath, textbookSummaryPath]);
    if (!summaryResult) {
      return null;
    }

    const { content, sourcePath } = summaryResult;
    const isTextbookSummary = sourcePath === textbookSummaryPath;
    const audioPath = isTextbookSummary
      ? `/content/textbooks/${bookId}/audio/${level}/${chunk}.mp3`
      : `/content/books/${bookId}/audio/${level}/${chunk}.mp3`;
    
    return {
      content,
      audioSrc: audioPath,
      audioTitle: `${bookId} Summary - ${level} Level`
    };
  } catch (error) {
    return null;
  }
};

/**
 * Load summary from JSON format (alternative format)
 * @param {string} bookId - Book identifier
 * @param {string} level - CEFR level
 * @param {string} chunk - Chunk/section identifier
 * @returns {Promise<Object|null>} Summary data or null
 */
export const loadSummaryJson = async (bookId, level, chunk) => {
  try {
    const summaryPath = `/content/books/${bookId}/${level}/summaries/${chunk}.json`;
    
    const response = await fetch(summaryPath);
    if (!response.ok) return null;
    
    const data = await response.json();
    
    return {
      content: data.summary || data.content || '',
      title: data.title || '',
      keyThemes: data.keyThemes || [],
      characters: data.characters || [],
      audioSrc: data.audioFile 
        ? `/content/books/${bookId}/${level}/audio/${data.audioFile}` 
        : '',
      audioTitle: data.audioTitle || `${bookId} Summary`
    };
  } catch (error) {
    return null;
  }
};

/**
 * Transform raw fragments to stanza format for rendering
 * @param {Array} fragments - Raw fragment data from JSON
 * @param {Object} options - Transformation options
 * @returns {Array} Transformed stanza array
 */
export const transformFragmentsToStanzas = (fragments, options = {}) => {
  if (!fragments || !Array.isArray(fragments)) return [];
  
  return fragments.map((fragment, index) => ({
    id: fragment.id || parseInt(fragment.number) || index + 1,
    number: fragment.number || String(index + 1),
    originalText: fragment.originalText || '',
    adaptedText: fragment.text || fragment.adaptedText || '',
    hasImage: fragment.hasImage || false,
    imageUrl: fragment.imageUrl || '',
    imageDescription: fragment.imageDescription || '',
    bookTitle: fragment.bookTitle || options.bookTitle || ''
  }));
};

/**
 * Update stanza numbers to use continuous fragment range
 * @param {Array} stanzas - Array of stanzas
 * @param {number} startNumber - Starting fragment number
 * @returns {Array} Updated stanzas with continuous numbering
 */
export const updateStanzaNumbers = (stanzas, startNumber) => {
  return stanzas.map((stanza, index) => ({
    ...stanza,
    number: String(startNumber + index)
  }));
};

/**
 * Get placeholder summary when actual summary is unavailable
 * @returns {Object} Placeholder summary object
 */
export const getPlaceholderSummary = () => ({
  content: 'Summary content will be available soon...',
  audioSrc: '',
  audioTitle: 'Summary Audio'
});
